import { Fragment, useEffect, useMemo, useState } from "react";
import OverlayLoader from "./OverlayLoader";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Hidden,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import {
  clearHolds,
  getSignedUrl,
  setActivityViewType,
  setFilterSearch,
  updateFilters,
} from "service/actions";
import { getFilterSearch, getReportFiles } from "service/selectors";
import SearchInput from "./SearchInput";
import { getUser } from "core/selectors";
import ActivityDatePicker from "service/ActivityDatePicker";
import { ExportIcon, FilterIcon } from "service/Icons";
import CloudDownload from "@material-ui/icons/CloudDownload";
import {
  ACTIVITY_PAGES,
  ACTIVITY_VIEW_TYPES,
  GENERAL_FILTER_TYPES,
} from "constants/activity.constants";
import DataTable from "./DataTable";
import ExtendedReport from "service/ExtendedActivityReport";

const baseLinkStyle = {
  fontFamily: "Sofia-bold",
  cursor: "pointer",
  color: "#09122B",
  fontWeight: 400,
  fontSize: 14,
  textDecoration: "underline",
  "& :hover": {
    color: "#E5196C",
  },
  "& svg": {
    margin: "0 5px",
  },
  "& span": {
    margin: "0 2px",
  },
};
const styles = (theme) => ({
  /* Row of search controls at the top.  Possibly two of these if they have admin access  */
  searchBar: {
    position: "relative",
    flexWrap: "wrap",
    display: "flex",
    alignItems: "center",
  },
  searchContainer: {
    display: "flex",
    alignItems: "stretch",
    flexWrap: "wrap",
    justifyContent: "space-between",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      // flexDirection:'row',
      alignItems: "center",
    },
  },
  row: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    flexWrap: "wrap",
    flexBasis: "1 1 auto",
    margin: "0 !important",
    "& >*": {
      margin: "10px 3px 0 0",
    },
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      width: "100%",
    },
  },
  flex: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "nowrap",
  },
  rowSmall: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    flex: "1 1 auto",
    margin: "0 !important",
    "& >*": {
      margin: "10px 3px 0 0",
    },
    flexWrap: "wrap",
    [theme.breakpoints.up("md")]: {
      flexWrap: "nowrap",
    },
  },
  title: {
    ...theme.typography.h6,
  },
  contentScroller: {
    display: "block",
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "auto",
    flexDirection: "column",
    "@media (min-height:700px)": {
      display: "flex",
    },
  },
  link: {
    ...baseLinkStyle,
  },
  linkDisabled: {
    ...baseLinkStyle,
    pointerEvents: "none",
  },
  sofia: {
    fontSize: 16,
    fontFamily: "Sofia !important",
    "& .MuiFormControlLabel-label": {
      fontSize: 16,
      fontFamily: "Sofia !important",
    },
  },
  sofiaBold: {
    fontSize: 16,
    fontFamily: "Sofia !important",
    "& .MuiFormControlLabel-label": {
      fontSize: 16,
      fontFamily: "Sofia !important",
    },
  },
  searchBox: {
    fontSize: 16,
    height: "auto",
    "& input, .MuiInputBase-input": {
      fontSize: 16,
      fontFamily: "Sofia !important",
    },
  },
});
function ActivityLayout({
  isLoading,
  onPdfClick,
  onCsvClick,
  csvDisabled,
  popups,
  renderFilters,
  classes,
  children,
  companyFilterType = GENERAL_FILTER_TYPES.ALL,
  popupFilters = {},
  startTime = null,
  endTime = null,
  dashboard = false,
  userAccounts = null,
  filterKey = "",
  page = ACTIVITY_PAGES.ACTIVITY,
  onTabChange = () => {},
  onStartTimeChange = () => {},
  onEndTimeChange = () => {},
  onCompanyFilterChange = () => {},
}) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [userFilterType] = useState(GENERAL_FILTER_TYPES.ALL);
  const [rememberFiltersChecked, setRememberFiltersChecked] = useState(false);
  const user = useSelector((state) => getUser(state));
  const reportFiles = useSelector((state) => getReportFiles(state));
  //   const activityViewType = useSelector((state) => getActivityViewType(state));
  const dispatch = useDispatch();
  const filterSearch = useSelector((state) => getFilterSearch(state));
  const handleTabChange = (e, num) => {
    if (typeof onTabChange === "function") {
      onTabChange();
    }
    setSelectedTab(num);
    if (num === 0) {
      dispatch(setActivityViewType(ACTIVITY_VIEW_TYPES.ALL));
    } else {
      dispatch(setActivityViewType(ACTIVITY_VIEW_TYPES.ARCHIVED));
    }
    dispatch(clearHolds());
  };
  const changeFilterSearch = (searchText) => {
    dispatch(clearHolds());
    dispatch(setFilterSearch(searchText));
  };
  const saveFilters = (force = false) => {
    if (force || rememberFiltersChecked) {
      window.localStorage.setItem(
        `${filterKey}filters`,
        JSON.stringify(popupFilters)
      );
      window.localStorage.setItem(`${filterKey}startTime`, startTime);
      window.localStorage.setItem(`${filterKey}endTime`, endTime);
      window.localStorage.setItem(
        `${filterKey}companyFilterType`,
        companyFilterType
      );
      window.localStorage.setItem(`${filterKey}userFilterType`, userFilterType);
    }
  };
  const fetchDate = (key, fallback) => {
    let date = window.localStorage.getItem(key);
    if (date) {
      date = new Date(date);
      if (date instanceof Date && !isNaN(date?.getTime())) {
        return date;
      }
    }
    return fallback;
  };
  const removeSavedFilters = () => {
    window.localStorage.removeItem(`${filterKey}startTime`);
    window.localStorage.removeItem(`${filterKey}endTime`);
    window.localStorage.removeItem(`${filterKey}filters`);
    window.localStorage.removeItem(`${filterKey}companyFilterType`);
    window.localStorage.removeItem(`${filterKey}userFilterType`);
  };

  const rememberMyFilters = (e) => {
    // let prevFilters = window.localStorage.getItem("filters");
    if (e.target.checked) {
      saveFilters(true);
    } else {
      removeSavedFilters();
    }
    setRememberFiltersChecked(e.target.checked);
  };
  const reportFileColumns = [
    {
      heading: "File Name",
      textAlign: "left",
      renderCell: (file) => {
        return String(file?.key)?.split("/")[1];
      },
    },
    {
      heading: "Created At",
      textAlign: "left",
      renderCell: (file) => {
        return (
          new Date(file.updateDate).toLocaleString("default", {
            month: "short",
            day: "numeric",
            weekday: "short",
          }) +
          ", " +
          new Date(file.updateDate)
            .toLocaleString("default", { timeStyle: "short" })
            .toLowerCase()
        );
      },
    },
    {
      heading: "",
      textAlign: "right",
      renderCell: (file) => {
        return (
          <IconButton
            onClick={() => {
              dispatch(
                getSignedUrl(file.reportFileId, (link) => {
                  window.open(link, "_blank");
                })
              );
            }}
          >
            <CloudDownload />
          </IconButton>
        );
      },
    },
  ];
  const loadFilters = () => {
    try {
      let prevPopupFilters = window.localStorage.getItem(`${filterKey}filters`);
      if (prevPopupFilters) {
        let loadedFilters = {};
        const savedFilters = JSON.parse(prevPopupFilters);
        for (const [key, value] of Object.entries(popupFilters)) {
          if (savedFilters[key]) {
            loadedFilters[key] = savedFilters[key];
          } else {
            loadedFilters[key] = value;
          }
        }
        setRememberFiltersChecked(true);
        dispatch(updateFilters(loadedFilters, page));
        onStartTimeChange(
          fetchDate(
            `${filterKey}startTime`,
            new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000)
          )
        );
        onEndTimeChange(fetchDate(`${filterKey}endTime`, new Date()));
        //   Company Filter type is either a string "all" or an int representing the accountId
        onCompanyFilterChange(
          window.localStorage.getItem(`${filterKey}companyFilterType`) ===
            GENERAL_FILTER_TYPES.ALL
            ? window.localStorage.getItem(`${filterKey}companyFilterType`)
            : parseInt(
                window.localStorage.getItem(`${filterKey}companyFilterType`)
              )
        );
      }
    } catch (e) {
      console.error("Error loading saved filters", e);
      setRememberFiltersChecked(false);
      dispatch(updateFilters(popupFilters, page));
      onStartTimeChange(
        new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000)
      );
      onEndTimeChange(new Date());
      onCompanyFilterChange(GENERAL_FILTER_TYPES.ALL);
    }
    // setLoaded(true);
  };
  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    saveFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterKey,
    popupFilters,
    startTime,
    endTime,
    companyFilterType,
    userFilterType,
  ]);
  const extendedReportAccounts = useMemo(() => {
    return (userAccounts || user.userAccountRoles || []).filter((uar) => {
      return uar.showExtendedReports === "Y" && uar.userRoleKey === "GLOBALADMIN";
    });
  }, [user, userAccounts]);
  return (
    <Fragment>
      {isLoading && <OverlayLoader />}
      {(popups || []).map((popup, index) => (
        <Dialog
          onClose={popup.onClose}
          open={popup.open}
          maxWidth={"md"}
          key={index}
        >
          <DialogTitle>{popup.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{popup.text}</DialogContentText>
            {popup?.component && popup.component}
            <DialogActions>
              {(popup.actions || []).map((action, index) => (
                <Button key={index} onClick={action.onClick}>
                  {action.text}
                </Button>
              ))}
            </DialogActions>
          </DialogContent>
        </Dialog>
      ))}
      <div style={{ height: "90vh", width: "100%" }}>
        <div className={classes.contentScroller}>
          <div>
            {/* Show Tabs on activity pages */}
            <Hidden xlDown={dashboard}>
              <Box>
                <Tabs value={selectedTab} onChange={handleTabChange}>
                  <Tab
                    label="All"
                    value={0}
                    style={{ textTransform: "none" }}
                  />
                  <Tab
                    label="Archive"
                    value={1}
                    style={{ textTransform: "none" }}
                  />
                  {/* Disable report files on chat activity page */}
                  {page !== ACTIVITY_PAGES.CHAT_ACTIVITY &&
                    reportFiles?.length > 0 && (
                      <Tab
                        label="Report Files"
                        value={2}
                        style={{ textTransform: "none" }}
                      />
                    )}
                  {extendedReportAccounts.length > 0 && (
                    <Tab
                      value={3}
                      label="Extended Report Files"
                      style={{ textTransform: "none" }}
                    />
                  )}
                </Tabs>
              </Box>
              <Divider style={{ marginBottom: 20 }} />
            </Hidden>
          </div>
          <Hidden xlDown={selectedTab === 3}>
            <div className={classes.searchBar}>
              {/* Show filters on activity pages */}
              <Hidden xlDown={dashboard || selectedTab === 2}>
                <div className={classes.searchContainer}>
                  <div className={classes.row}>
                    <div className={classes.row}>
                      <div className={classes.rowSmall}>
                        <SearchInput
                          classes={{
                            searchInput: classes.searchBox,
                          }}
                          value={filterSearch}
                          onCloseIconClick={() => {
                            changeFilterSearch("");
                          }}
                          onChange={(event) => {
                            changeFilterSearch(event.target.value);
                          }}
                        />
                        <Hidden
                          xlDown={
                            userAccounts
                              ? userAccounts.length === 0
                              : user.userAccountRoles.length === 0
                          }
                        >
                          <div className={classes.searchBox}>
                            <Select
                              value={companyFilterType}
                              onChange={(event) => {
                                onCompanyFilterChange(event.target.value);
                              }}
                              input={
                                <OutlinedInput style={{ width: "100%" }} />
                              }
                            >
                              <MenuItem
                                className={classes.sofia}
                                value={GENERAL_FILTER_TYPES.ALL}
                              >
                                All Companies
                              </MenuItem>

                              {(
                                userAccounts ||
                                user.userAccountRoles ||
                                []
                              ).map((filterAccount) => (
                                <MenuItem
                                  className={classes.sofia}
                                  key={filterAccount.accountId}
                                  value={filterAccount.accountId}
                                >
                                  {filterAccount.accountName}
                                </MenuItem>
                              ))}
                            </Select>
                          </div>
                        </Hidden>
                      </div>
                      <div className={classes.rowSmall}>
                        <Hidden xlDown={selectedTab === 2}>
                          <ActivityDatePicker
                            page={page}
                            startTime={startTime}
                            endTime={endTime}
                            setStartTime={(value) => {
                              onStartTimeChange(
                                value !== null ? value.toJSDate() : null
                              );
                            }}
                            setEndTime={(value) => {
                              onEndTimeChange(
                                value !== null ? value.toJSDate() : null
                              );
                            }}
                          />
                        </Hidden>
                      </div>
                    </div>
                    <FormControlLabel
                      className={classes.sofia}
                      control={
                        <Checkbox
                          size="small"
                          style={{ color: "#7F2062" }}
                          checked={rememberFiltersChecked}
                          onClick={(event) => event.stopPropagation()}
                          onChange={rememberMyFilters}
                        />
                      }
                      label="Remember my filters"
                    />
                  </div>
                  <div className={classes.row}>
                    {!!renderFilters && (
                      <Typography
                        onClick={renderFilters.onClick}
                        className={classes.link}
                      >
                        <span className={classes.flex}>
                          <FilterIcon />
                          <span>{renderFilters.filterListText}</span>
                        </span>
                      </Typography>
                    )}

                    {!!onPdfClick && (
                      <Tooltip
                        enterTouchDelay={10}
                        title="Minimum of 3 characters required to search & maximum of one month of data can be retrieved at a time"
                        aria-label="add"
                      >
                        <span>
                          <Typography
                            onClick={onPdfClick}
                            className={
                              filterSearch.trim().length <= 2
                                ? classes.linkDisabled
                                : classes.link
                            }
                          >
                            <span className={classes.flex}>
                              <ExportIcon />
                              <span>Export PDF</span>
                            </span>
                          </Typography>
                        </span>
                      </Tooltip>
                    )}

                    {!!onCsvClick && (
                      <Typography
                        onClick={onCsvClick}
                        className={
                          csvDisabled ? classes.linkDisabled : classes.link
                        }
                      >
                        <span className={classes.flex}>
                          <ExportIcon />
                          <span>Export CSV</span>
                        </span>
                      </Typography>
                    )}
                  </div>
                </div>
              </Hidden>
            </div>
          </Hidden>
          <Hidden xlDown={selectedTab !== 3}>
            <ExtendedReport
              userAccounts={extendedReportAccounts}
            />
          </Hidden>
          {/* If tab 0 or 1 are selected show the activities */}
          <Hidden xlDown={selectedTab === 2 || selectedTab === 3}>
            {children}
          </Hidden>
          {/* Render the report files (disable on chat activity page) */}
          <Hidden
            xlDown={selectedTab !== 2 || page === ACTIVITY_PAGES.CHAT_ACTIVITY}
          >
            <DataTable data={reportFiles || []} columns={reportFileColumns} />
          </Hidden>
        </div>
      </div>
      {/* {isLoading} */}
    </Fragment>
  );
}

export default withStyles(styles)(ActivityLayout);
